import { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../components/sections/Navbar";
import PageHeader from "../components/sections/PageHeader";
import Breadcrumb from "../components/ui/Breadcrumb";
import Button from "../components/ui/Button";
import SearchableSelect from "../components/ui/SearchableSelect";
import {
  getLoonOpZandTextAnswers,
  updateLoonOpZandTextAnswer,
} from "../api/loonopzandDashboard";
import { useAuth } from "../context/AuthContext";
import "./LoonOpZandTextAnswers.css";

function ProgressCard({ label, progress }) {
  const total = progress?.total || 0;
  const completed = progress?.completed || 0;
  const remaining = progress?.remaining || 0;
  const percentage = progress?.percentage ?? 100;

  return (
    <section className="text-answers__progress-card">
      <span>{label}</span>
      <strong>{remaining} nog te bewerken</strong>
      <p>{completed} van {total} ingevuld</p>
      <div className="text-answers__progress" aria-label={`${percentage}% voltooid`}>
        <div style={{ width: `${percentage}%` }} />
      </div>
    </section>
  );
}

function EditedAnswerCombobox({ answerId, value, suggestions, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const search = value.trim().toLocaleLowerCase("nl");
  const filteredSuggestions = suggestions.filter((suggestion) =>
    !search || suggestion.value.toLocaleLowerCase("nl").includes(search)
  );
  const listId = `edited-answer-options-${answerId}`;

  return (
    <div className="text-answers__combobox-wrap">
      <input
        id={`edited-answer-${answerId}`}
        className="text-answers__combobox"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={isOpen}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        placeholder="Kies een bestaande waarde of typ een nieuwe"
        autoComplete="off"
      />
      {isOpen && suggestions.length > 0 && (
        <div className="text-answers__combobox-options" id={listId} role="listbox">
          {filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((suggestion) => (
              <button
                type="button"
                role="option"
                aria-selected={value === suggestion.value}
                key={suggestion.value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(suggestion.value);
                  setIsOpen(false);
                }}
              >
                <span>{suggestion.value}</span>
                <small>{suggestion.count}× gebruikt</small>
              </button>
            ))
          ) : (
            <p>Geen bestaande waarden gevonden. Je kunt deze nieuwe waarde opslaan.</p>
          )}
        </div>
      )}
    </div>
  );
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) throw new Error("Kopiëren is niet beschikbaar.");
}

export default function LoonOpZandTextAnswers() {
  const { authorizedFetch } = useAuth();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [questionnaire, setQuestionnaire] = useState("");
  const [question, setQuestion] = useState("");
  const [issue, setIssue] = useState("");
  const [answerStatus, setAnswerStatus] = useState("all");
  const [answers, setAnswers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [copied, setCopied] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const loadAnswers = useCallback(async (signal, quiet = false) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const payload = await getLoonOpZandTextAnswers(authorizedFetch, {
        questionnaire,
        question,
        issue,
        status: answerStatus,
        page,
        signal,
      });
      const nextQuestionnaires = payload?.questionnaires || [];
      setQuestionnaires(nextQuestionnaires);
      setAnswers(payload?.answers || []);
      setSuggestions(payload?.suggestions || []);
      setPagination(payload?.pagination || null);
      setDrafts(
        Object.fromEntries((payload?.answers || []).map((answer) => [answer.id, answer.edited || ""]))
      );

      if (!questionnaire && nextQuestionnaires.length) {
        const firstQuestionnaire = nextQuestionnaires[0];
        setQuestionnaire(firstQuestionnaire.slug);
        setQuestion(firstQuestionnaire.questions?.[0]?.field || "");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message || "De tekstantwoorden konden niet worden geladen.");
      }
    } finally {
      if (!signal?.aborted && !quiet) setLoading(false);
    }
  }, [answerStatus, authorizedFetch, issue, page, question, questionnaire]);

  useEffect(() => {
    const controller = new AbortController();
    loadAnswers(controller.signal);
    return () => controller.abort();
  }, [loadAnswers]);

  const activeQuestionnaire = useMemo(
    () => questionnaires.find((item) => item.slug === questionnaire),
    [questionnaire, questionnaires]
  );
  const activeQuestion = useMemo(
    () => activeQuestionnaire?.questions?.find((item) => item.field === question),
    [activeQuestionnaire, question]
  );

  const questionnaireOptions = questionnaires.map((item) => ({
    value: item.slug,
    label: item.label,
  }));
  const questionOptions = (activeQuestionnaire?.questions || []).map((item) => ({
    value: item.field,
    label: item.label,
  }));
  const issueOptions = (activeQuestionnaire?.issues || []).map((item) => ({
    value: item.value,
    label: item.label,
  }));

  const handleQuestionnaireChange = (nextSlug) => {
    const nextQuestionnaire = questionnaires.find((item) => item.slug === nextSlug);
    setQuestionnaire(nextSlug);
    setQuestion(nextQuestionnaire?.questions?.[0]?.field || "");
    setIssue("");
    setPage(1);
    setSaved({});
  };

  const handleSave = async (answer) => {
    setSaving((current) => ({ ...current, [answer.id]: true }));
    setSaved((current) => ({ ...current, [answer.id]: false }));
    setError("");
    try {
      await updateLoonOpZandTextAnswer(
        authorizedFetch,
        questionnaire,
        answer.id,
        question,
        drafts[answer.id] || ""
      );
      setSaved((current) => ({ ...current, [answer.id]: true }));
      await loadAnswers(undefined, true);
    } catch (err) {
      setError(err.message || "Het antwoord kon niet worden opgeslagen.");
    } finally {
      setSaving((current) => ({ ...current, [answer.id]: false }));
    }
  };

  const handleCopy = async (answer) => {
    try {
      await copyTextToClipboard(answer.original);
      setCopied((current) => ({ ...current, [answer.id]: true }));
      window.setTimeout(() => {
        setCopied((current) => ({ ...current, [answer.id]: false }));
      }, 1600);
    } catch (err) {
      setError(err.message || "Het antwoord kon niet worden gekopieerd.");
    }
  };

  return (
    <div>
      <Navbar />
      <main className="container text-answers">
        <Breadcrumb
          icon="home-line.png"
          items={["Dashboards", "Loon op Zand"]}
          current="Tekstantwoorden bewerken"
        />
        <PageHeader title="Tekstantwoorden bewerken" />
        <p className="text-answers__intro">
          Bewaar de oorspronkelijke invoer en leg daarnaast een geanonimiseerde of
          gegroepeerde waarde vast.
        </p>

        <section className="text-answers__filters">
          <SearchableSelect
            label="Vragenlijst"
            name="questionnaire"
            value={questionnaire}
            options={questionnaireOptions}
            onChange={handleQuestionnaireChange}
            clearable={false}
          />
          <SearchableSelect
            label="Vraag"
            name="question"
            value={question}
            options={questionOptions}
            wrapperClassName="text-answers__question-filter"
            onChange={(value) => {
              setQuestion(value);
              setPage(1);
              setSaved({});
            }}
            clearable={false}
          />
          <SearchableSelect
            label="Periode"
            name="issue"
            value={issue}
            options={issueOptions}
            onChange={(value) => {
              setIssue(value);
              setPage(1);
              setSaved({});
            }}
            placeholder="Alle perioden"
            clearLabel="Alle perioden"
          />
        </section>

        {activeQuestionnaire && activeQuestion && (
          <div className="text-answers__progress-grid">
            <ProgressCard label="Deze vragenlijst" progress={activeQuestionnaire} />
            <ProgressCard label="Deze vraag" progress={activeQuestion} />
          </div>
        )}

        {error && <p className="text-answers__error" role="alert">{error}</p>}
        {loading && <p>Tekstantwoorden laden…</p>}
        {!loading && !error && questionnaires.length === 0 && (
          <div className="text-answers__empty">
            Er zijn geen actieve vragenlijsten met bewerkbare tekstantwoorden.
          </div>
        )}
        {!loading && !error && activeQuestion && (
          <section className="text-answers__answers" aria-label="Tekstantwoorden">
            <div className="text-answers__answers-heading">
              <h2>{activeQuestion?.label}</h2>
              <div className="text-answers__status-filter" aria-label="Filter op antwoordstatus">
                {[
                  { value: "all", label: "Alle", count: activeQuestion?.total || 0 },
                  { value: "remaining", label: "Nog te doen", count: activeQuestion?.remaining || 0 },
                  { value: "completed", label: "Ingevuld", count: activeQuestion?.completed || 0 },
                ].map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={answerStatus === option.value ? "active" : ""}
                    onClick={() => {
                      setAnswerStatus(option.value);
                      setPage(1);
                    }}
                  >
                    {option.label} <span>{option.count}</span>
                  </button>
                ))}
              </div>
            </div>
            {answers.length === 0 ? (
              <div className="text-answers__empty">
                {answerStatus === "completed"
                  ? "Er zijn nog geen ingevulde antwoorden voor deze selectie."
                  : answerStatus === "remaining"
                    ? "Alle antwoorden voor deze selectie zijn ingevuld."
                    : "Voor deze vraag zijn geen tekstantwoorden gevonden."}
              </div>
            ) : (
              <>
                <div className="text-answers__table-header" aria-hidden="true">
                  <span>Antwoord</span>
                  <span>Oorspronkelijk antwoord</span>
                  <span>Bewerkt antwoord</span>
                  <span>Actie</span>
                </div>
                <div className="text-answers__list">
                {answers.map((answer, index) => (
              <article
                className={`text-answers__row ${answer.edited ? "is-completed" : "is-remaining"}`}
                key={answer.id}
              >
                <div className="text-answers__row-heading">
                  <strong>Antwoord {(pagination?.page - 1) * pagination?.page_size + index + 1}</strong>
                  <span className="text-answers__period">{answer.issue}</span>
                  <span className="text-answers__row-status">
                    <i aria-hidden="true" />
                    {answer.edited ? "Ingevuld" : "Nog te doen"}
                  </span>
                </div>
                <div className="text-answers__field text-answers__original-field">
                  <label>Oorspronkelijk antwoord</label>
                  <div className="text-answers__original">
                    <span>{answer.original}</span>
                    <button
                      type="button"
                      className="text-answers__copy"
                      onClick={() => handleCopy(answer)}
                      aria-label="Oorspronkelijk antwoord kopiëren"
                      title="Oorspronkelijk antwoord kopiëren"
                    >
                      <img src="/icons/copy.png" alt="" />
                      {copied[answer.id] ? "Gekopieerd" : "Kopieer"}
                    </button>
                  </div>
                </div>
                <div className="text-answers__field text-answers__edited-field">
                  <label htmlFor={`edited-answer-${answer.id}`}>Bewerkt antwoord</label>
                  <EditedAnswerCombobox
                    answerId={answer.id}
                    value={drafts[answer.id] || ""}
                    suggestions={suggestions}
                    onChange={(value) => {
                      setDrafts((current) => ({ ...current, [answer.id]: value }));
                      setSaved((current) => ({ ...current, [answer.id]: false }));
                    }}
                  />
                </div>
                <div className="text-answers__save-row">
                  {saved[answer.id] && <span>Opgeslagen</span>}
                  <Button
                    onClick={() => handleSave(answer)}
                    disabled={saving[answer.id] || (drafts[answer.id] || "") === answer.edited}
                  >
                    {saving[answer.id] ? "Opslaan…" : "Opslaan"}
                  </Button>
                </div>
              </article>
                ))}
                </div>
              </>
            )}
          </section>
        )}

        {pagination?.page_count > 1 && (
          <div className="text-answers__pagination">
            <span>Pagina {pagination.page} van {pagination.page_count}</span>
            <div>
              <Button variant="secondary" disabled={pagination.page <= 1} onClick={() => setPage((value) => value - 1)}>
                Vorige
              </Button>
              <Button variant="secondary" disabled={pagination.page >= pagination.page_count} onClick={() => setPage((value) => value + 1)}>
                Volgende
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
